import { v4 as uuid } from "uuid";
import { MapState, MapModuleLayer, LayerStorageScope, LayerDrawingControl, LayerDrawingControls, Source, SitemapVersionLevel } from "@iventis/map-engine/src/types/store-schema";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { Map } from "@iventis/domain-model/model/map";
import { MapMode } from "@iventis/map-engine/src/machines/map-machines.types";
import { LayerStyle } from "@iventis/domain-model/model/layerStyle";
import { analysisLineLayerId, analysisPolygonLayerId, COMMENT_VECTOR_SOURCE_ID } from "@iventis/map-engine/src/bridge/constants";
import { AnalysisType } from "@iventis/domain-model/model/analysisType";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { setLayerStyle } from "@iventis/map-engine/src/utilities/layer.helpers";
import { isAnalysisLayer, isLocalOnlyModelLayer } from "@iventis/map-engine/src/utilities/state-helpers";
import { PagedResult } from "@iventis/domain-model/model/pagedResult";
import { getAssetsByIds } from "@iventis/api-helpers";
import { DomainLayer, MapGlobalState } from "@iventis/map-engine/src/state/map.state.types";
import { ViewByStatus } from "@iventis/map-engine/src/types/layer-view-by";
import { setLocalMapObjectsDateFilterValue } from "@iventis/map-engine/src/utilities/date-time-filtering";
import { parseISO } from "date-fns";
import { MapObjectAggregated } from "@iventis/domain-model/model/mapObjectAggregated";
import { IventisRequestConfig } from "@iventis/api-helpers/src/api.types";
import { assetsApi, mappingApi } from "@iventis/api/src/api";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { checkModeIsPresent } from "@iventis/map-engine/src/machines/mode-machine.helpers";
import { getAssetSignature, parseDataFieldValuesWithRepeatedTimeRange } from "@iventis/utilities";
import { SelectedMapObject, LocalGeoJson, LocalGeoJsonObject } from "@iventis/map-types";
import { getLayerStyle } from "@iventis/layer-style-helpers/src/get-layer-style-helpers";
import { DigitalTwinConnectionError } from "@iventis/digital-twin/src/digitial-twin-types";
import { Polygon } from "geojson";
import { MapSitemapConfig } from "@iventis/domain-model/model/mapSitemapConfig";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { DigitalTwinLicence } from "@iventis/domain-model/model/digitalTwinLicence";
import { baseURL } from "./map.slice";
import { Sitemap } from "./map.slice.types";
import { GOOGLE_MAPS_SATELLITE_MAP_BACKGROUND_NAME } from "../map-constants";

/* eslint-disable no-param-reassign */
export function stampValue<ValueType>(stateEntry: { stamp: string; value: ValueType }, value: ValueType) {
    stateEntry.stamp = uuid();
    stateEntry.value = value;
}

export function setMode(state: MapState, mode: MapMode) {
    state.mode.value = mode;
    state.mode.stamp = uuid();
}

export function createNewLocalId(state: MapState) {
    const id = uuid();
    stampValue(state.localToRemoteMapObjectIdsMap, {
        ...state.localToRemoteMapObjectIdsMap.value,
        [id]: undefined,
    });
    return id;
}

export function setIdPair(state: MapState, localId: string, remoteId: string) {
    if (localId === undefined) {
        throw new Error("At least a local ID must be provided");
    }
    stampValue(state.localToRemoteMapObjectIdsMap, {
        ...state.localToRemoteMapObjectIdsMap.value,
        [localId]: remoteId,
    });
}

export function setLocalToRemoteMapObjectIds(state: MapState, pairs: { key: string; value: string }[]) {
    stampValue(state.localToRemoteMapObjectIdsMap, {
        ...state.localToRemoteMapObjectIdsMap.value,
        ...pairs.reduce(
            (cum, { key, value }) => ({
                ...cum,
                [key]: value,
            }),
            {} as { [localId: string]: string }
        ),
    });
}

export function composeNewObjects(state: MapState, objectIds: string[]) {
    stampValue(state.localToRemoteMapObjectIdsMap, {
        ...state.localToRemoteMapObjectIdsMap.value,
        ...objectIds.reduce((cum, objectId) => ({ ...cum, [objectId]: undefined }), {}),
    });
}

/** Takes the new object selection, and updates the selected property of the affected analysis objects */
export function updateAnalysisObjectsSelectionProperty(state: MapState, newSelection: SelectedMapObject[]) {
    state.mapObjectsSelected.value.forEach((prev) => {
        if (isAnalysisLayer(prev.layerId) && !newSelection.some((next) => prev.objectId === next.objectId)) {
            const currentFeature = state.geoJSON.value[prev.layerId]?.find?.((object) => object.objectId === prev.objectId)?.feature;
            if (currentFeature != null) {
                currentFeature.properties.selected = undefined;
            }
        }
    });
    newSelection.forEach((next) => {
        if (isAnalysisLayer(next.layerId) && !state.mapObjectsSelected.value.some((prev) => prev.objectId === next.objectId)) {
            const currentFeature = state.geoJSON.value[next.layerId]?.find?.((object) => object.objectId === next.objectId)?.feature;
            if (currentFeature != null) {
                currentFeature.properties.selected = "selected";
            }
        }
    });
    stampValue(state.geoJSON, state.geoJSON.value);
}

export function updateDigitalTwinLicence(state: MapState, licence: DigitalTwinLicence, error: DigitalTwinConnectionError) {
    state.digitalTwinEngineData.currentDigitalTwinLicence = licence;
    state.digitalTwinEngineData.connectionError = error;
}

export function updateLayerStyle(state: MapGlobalState, layerId: string, styledLayer: MapLayer) {
    const style: LayerStyle = getLayerStyle(styledLayer);
    let layer = state.mapModule.layers.value.find((layer) => layer.id === layerId);
    if (layer == null) {
        throw new Error(`Layer with id of ${layerId} can not be found`);
    }
    layer = setLayerStyle(layer, style);
    updateMapLayerWithStamp(state, layer);
}

export function updateMapLayerWithStamp(state: MapGlobalState, layer: MapModuleLayer) {
    const newLayers: MapModuleLayer[] = [...state.mapModule.layers.value.filter((value) => value.id !== layer.id), { ...layer, stamp: uuid() }];
    stampValue(state.mapModule.layers, newLayers);
}

export function updateLayerSelectedValue(state: MapGlobalState, selectedLayerIds: string[]) {
    const stateLayersCopy = [...state.mapModule.layers.value];
    let changed = false;
    stateLayersCopy.forEach((layer) => {
        const selected = selectedLayerIds.includes(layer.id);
        if (selected !== layer.selected) {
            layer.selected = selectedLayerIds.includes(layer.id);
            changed = true;
        }
    });
    return { layers: stateLayersCopy, changed } as const;
}

// Sets the basic requirements of the map to be able to render the map-root component
export function setMap(state: MapGlobalState, map: Map, tileBounds: { comments: Polygon; objects: Polygon }) {
    state.mapModule.mapId = map.id;
    state.mapName = map.name;
    state.savedMapViews = map.savedMapViews;
    state.savedMapViewDefault = map.defaultSavedMapViewId;
    const defaultsavedMapView = map.savedMapViews && map.defaultSavedMapViewId ? map.savedMapViews.find((mv) => mv.id === map.defaultSavedMapViewId) : null;
    if (defaultsavedMapView) {
        const { longitude, latitude, zoom, bearing, pitch } = defaultsavedMapView.position;
        stampValue(state.mapModule.position, { lng: longitude, lat: latitude, zoom, bearing, pitch, source: Source.HOST }); // Source Init because engine constructor will use this value
        state.mapModule.currentLevel = defaultsavedMapView.level;
    }
    if (map.layers != null) {
        const parsedLayers = map.layers.map((l) => convertLayerDate(l));
        const layers = [...state.mapModule.layers.value, ...parsedLayers.map((lyr) => parseLayerToIventisLayer(lyr))];
        stampValue(state.mapModule.layers, layers || []);
    }
    stampValue(state.mapModule.tileSources, {
        objects: {
            bounds: tileBounds.objects,
            tiles: [
                {
                    name: "iventis",
                    tiles: [`${baseURL}map/${map.id}/tiles/{z}/{x}/{y}.pbf?ignoreZoomOptimisation=false`],
                    type: "vector",
                },
            ],
        },
        comments: {
            bounds: tileBounds.comments,
            tiles: [
                {
                    name: COMMENT_VECTOR_SOURCE_ID,
                    tiles: [`${baseURL}maps/${map.id}/comments/tiles/{z}/{x}/{y}.pbf?ignoreZoomOptimisation=false`],
                    type: "vector",
                },
            ],
        },
    });
    state.mapModule.selectedMapBackgroundId = map.backgroundId;
    state.savedBackgroundDefault = map.backgroundId;
    stampValue(state.mapModule.terrain3D, {
        enabled: map.terrainEnabled,
        exaggeration: state.mapModule.terrain3D.value.exaggeration,
        customTerrains: state.mapModule.terrain3D.value.customTerrains,
    });
    stampValue(state.mapModule.globe, map.globeEnabled);
    state.sitemapConfigs = map.sitemapConfigs;
    stampValue(state.mapModule.buildings3D, map.buildings3DEnabled);
    stampValue(state.mapModule.streetNames, map.streetNamesEnabled);
}

export const getLayerThumbnailAssets = async (layers: MapLayer[], maxRetries = 0, retries = 0): Promise<{ assetUrl: string; layerId: string }[]> => {
    const layerIdsAssetIds = layers.map((layer) => ({ layerId: layer.id, assetId: layer.id }));
    // Want the unique asset ids
    const assetIds = [...new Set(layerIdsAssetIds.map(({ assetId }) => assetId))];
    const data = await getAssetsByIds(assetIds, assetsApi);
    // If any have failed and we want to retry then do so (reason for failure could be a 404 if the asset hasn't been created yet)
    if (data.failed?.length > 0 && retries < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, 4000));
        const result = await getLayerThumbnailAssets(layers, maxRetries, retries + 1);
        return result;
    }
    const found = data.found.filter((r) => r != null); // Remove filter after BUG: 11019
    const urlsWithLayerIds = layerIdsAssetIds.reduce((cum, { layerId, assetId }) => {
        const asset = found.find((asset) => asset.id === assetId);
        if (asset == null) {
            return cum;
        }
        const { assetUrl, authoritySignature } = asset;
        cum.push({
            assetUrl: getAssetSignature(assetUrl, authoritySignature),
            layerId,
        });
        return cum;
    }, []);
    return urlsWithLayerIds;
};

// Sets the map view is the active one
export function setMapView(state: MapGlobalState, payload: string) {
    const mapView = state.savedMapViews.find((mv) => mv.id === payload);
    if (mapView) {
        stampValue(state.mapModule.position, {
            lng: mapView.position.longitude,
            lat: mapView.position.latitude,
            zoom: mapView.position.zoom,
            bearing: mapView.position.bearing,
            pitch: mapView.position.pitch,
            source: Source.HOST,
        });
        state.mapModule.currentLevel = mapView.level;
    }
}

export function set3DTerrian(state: MapGlobalState, payload: boolean) {
    stampValue(state.mapModule.terrain3D, { enabled: payload, exaggeration: 1, customTerrains: state.mapModule.terrain3D?.value?.customTerrains ?? [] });
}

export function setGlobe(state: MapGlobalState, payload: boolean) {
    stampValue(state.mapModule.globe, payload);
}

/**
 * Assumes any inputted layer is remote
 * @param layer
 * @returns
 */
export function parseLayerToIventisLayer(layer: MapLayer & Partial<DomainLayer>, remote = true): DomainLayer {
    return {
        ...layer,
        storageScope: isLocalOnlyModelLayer(layer) ? LayerStorageScope.LocalOnly : LayerStorageScope.LocalAndTiles,
        source: isLocalOnlyModelLayer(layer) ? layer.id : "iventis",
        stamp: "",
        selected: layer.selected,
        viewBy: layer.viewBy ?? ViewByStatus.OBJECT,
        viewById: layer.viewById ?? null,
        remote,
        drawingControls: getDrawingControlsForStyleType(layer.styleType),
    };
}

function getDrawingControlsForStyleType(styleType: StyleType): LayerDrawingControls {
    switch (styleType) {
        case StyleType.Line:
        case StyleType.LineModel:
        case StyleType.Area:
        case StyleType.AreaModel:
            return {
                [LayerDrawingControl.ROTATION_HANDLE]: true,
                [LayerDrawingControl.MID_POINT_HANDLE]: true,
                [LayerDrawingControl.COORDINATE_HANDLE]: true,
            };
        case StyleType.Model:
        case StyleType.Icon:
            return {
                [LayerDrawingControl.ROTATION_HANDLE]: true,
                [LayerDrawingControl.MID_POINT_HANDLE]: false,
                [LayerDrawingControl.COORDINATE_HANDLE]: false,
            };
        default:
            return {
                [LayerDrawingControl.ROTATION_HANDLE]: false,
                [LayerDrawingControl.MID_POINT_HANDLE]: false,
                [LayerDrawingControl.COORDINATE_HANDLE]: false,
            };
    }
}

export function removeObjects(state: MapGlobalState, objectIds: string[]) {
    // Update object selection to not include the deleted objects
    const newMapObjectsSelected = state.mapModule.mapObjectsSelected.value.filter((object) => !objectIds.some((deletedId) => deletedId === object.objectId));
    stampValue(state.mapModule.mapObjectsSelected, newMapObjectsSelected);
    // Remove local object geometries for the removed object IDs
    const newLocalObjects = Object.entries(state.mapModule.geoJSON.value).reduce(
        (cum, [layerId, objects]) => ({
            ...cum,
            // For this particular layer, remove all the object geometries that have IDs matching our removed object IDs
            [layerId]: objects.filter((object) => !objectIds.some((deletedObjectId) => object.objectId === deletedObjectId)),
        }),
        {} as LocalGeoJson
    );
    stampValue(state.mapModule.geoJSON, newLocalObjects);
    state.incompleteDrawingObjects = state.incompleteDrawingObjects.filter((objectId) => !objectIds.includes(objectId));
}

/** Gets objects that will always be local only and not possible through tiles (models) */
export const getRemoteObjects = async (filter: IventisRequestConfig, signal?: AbortSignal) =>
    mappingApi.get<PagedResult<MapObjectAggregated[]>>(`/maps/${filter.params.mapId}/map_objects/filter`, { ...filter, signal });

// Extracts dataFieldValues (which is either a string or DataFieldListItem) and adds them to the local GeoJsonObject's properties
export const extractDataFieldValues = (dataFieldValues: MapObject["dataFieldValues"], object: LocalGeoJsonObject) => {
    Object.entries(dataFieldValues).forEach(([dataFieldId, dataFieldValue]) => {
        let value = dataFieldValue;
        if (value == null) {
            delete object.feature.properties[dataFieldId];
            return;
        }
        const isObject = typeof dataFieldValue === "object";
        const isArray = Array.isArray(dataFieldValue);
        if (isObject && !isArray && dataFieldValue != null) {
            value = (dataFieldValue as DataFieldListItem).id;
        }

        object.feature.properties = {
            ...object.feature.properties,
            [dataFieldId]: value,
        };
    });
};

export const patchLocalMapObjects = (mapObjects: Partial<MapObject>[], geoJSON: LocalGeoJson) => {
    Object.entries(geoJSON).forEach(([, existingMapObjects]) => {
        existingMapObjects.forEach((existingMapObject) => {
            const mapObjectUpdate = mapObjects.find((updatedMapObject) => updatedMapObject.id === existingMapObject.objectId);
            if (mapObjectUpdate !== undefined) {
                existingMapObject.feature.properties = { ...existingMapObject.feature.properties };
                extractDataFieldValues(mapObjectUpdate.dataFieldValues, existingMapObject);
            }
        });
    });
    return geoJSON;
};

/**
 * Converts layer dates from the backend for the given layer to the correct javascript object
 * Return the corrected layer
 */
export const convertLayerDate = (layer: MapLayer): MapLayer => {
    const newLayer = layer;
    newLayer.createdAt = layer.createdAt != null ? parseISO(layer.createdAt.toString()) : undefined;
    newLayer.lastUpdatedAt = layer.lastUpdatedAt != null ? parseISO(layer.lastUpdatedAt.toString()) : undefined;
    newLayer.lastViewedAt = layer.lastViewedAt != null ? parseISO(layer.lastViewedAt.toString()) : undefined;
    return newLayer;
};

/** Checks if a layer has image attribute and if it has update the map objects */
export const patchImageAttributes = (updatedMapObjects: MapObject[], geoJSON: LocalGeoJson, layers: DomainLayer[]) => {
    // Flag for if any changes have been made
    let changes = false;
    layers.forEach((layer) => {
        // Check if the layer has an datafield image attributes
        const hasLayerHaveImageAttribute = layer.dataFields.some((df) => df.type === DataFieldType.Image);
        // Check if that layer has any map object updates
        const hasLayerGotAUpdatedMapObject = updatedMapObjects.some((mapObject) => mapObject.layerId === layer.id);
        if (hasLayerHaveImageAttribute && hasLayerGotAUpdatedMapObject) {
            // Cycle through the map object updates and update their attribute values
            updatedMapObjects.forEach((updatedMapObject) => {
                const mapObject = geoJSON[updatedMapObject.layerId].find((mapObject) => updatedMapObject.id === mapObject.objectId);
                if (mapObject != null) {
                    changes = true;
                    mapObject.feature.properties = { ...mapObject.feature.properties };
                    const dataFieldValuesWithLocalRepeatedTimeRanges = parseDataFieldValuesWithRepeatedTimeRange(updatedMapObject.dataFieldValues, "number");
                    extractDataFieldValues(dataFieldValuesWithLocalRepeatedTimeRanges, mapObject);
                }
            });
        }
    });
    return changes ? geoJSON : null;
};

/** Given an update date filter, will update all the relevant state for the map */
export function updateMapStateWithFilterChange(state: MapGlobalState, updatedFilter?: MapGlobalState["mapModule"]["datesFilter"]["value"]) {
    if (updatedFilter == null) {
        updatedFilter = state.mapModule.datesFilter.value;
    }

    const { datesFilter, geoJSON, layers, mapObjectsSelected, mode } = state.mapModule;
    // If in composition mode we don't want to filter the object currently being drawn, when we finish drawing we will filter them
    const isCompositionMode = checkModeIsPresent(mode.value, "composition");
    const mapObjectIdsNotToFilter = isCompositionMode ? mapObjectsSelected.value.map((mo) => mo.objectId) : [];
    // Set which map objects should be filtered
    const filteredMapObjectIds = setLocalMapObjectsDateFilterValue(geoJSON.value, layers.value, updatedFilter.day, updatedFilter.time, mapObjectIdsNotToFilter);

    // Stamp all values
    stampValue(datesFilter, updatedFilter);
    stampValue(geoJSON, geoJSON.value);
    if (!isCompositionMode) {
        stampValue(
            mapObjectsSelected,
            mapObjectsSelected.value.filter((mo) => !filteredMapObjectIds.includes(mo.objectId))
        );
    }
}

export function isAnalysisToolSelectionValid(layerId: string, analysisToolSelection: AnalysisType) {
    switch (layerId) {
        case analysisLineLayerId:
            return analysisToolSelection === AnalysisType.Distance || analysisToolSelection === AnalysisType.FlowRate || analysisToolSelection === AnalysisType.WalkTime;
        case analysisPolygonLayerId:
            return analysisToolSelection === AnalysisType.Area || analysisToolSelection === AnalysisType.Capacity || analysisToolSelection === AnalysisType.Count;
        default:
            return false;
    }
}

/**
 * Given the current sitemaps, sitemap config and current level, returns the selected sitemap version level ids and the type of sitemap version they belong to
 * @param sitemaps Array of sitemaps
 * @param sitemapConfig Array of sitemap configurations
 * @param currentLevel Level index to get
 * @returns Array of objects with level id and version type (TwoDimensional or ThreeDimensional)
 */
export function getSelectedSitemapVersionLevels(sitemaps: Sitemap[], sitemapConfig: MapSitemapConfig[], currentLevel: number) {
    return sitemaps.flatMap((sitemap) => {
        const config = sitemapConfig.find((c) => c.sitemapId === sitemap.id);
        const levels: SitemapVersionLevel[] = [];

        if (config == null) {
            // Find newest 2d version
            const sitemap2dLevel = getNewestSitemapVersionLevelByType(sitemap, currentLevel, SitemapVersionType.TwoDimensional);
            if (sitemap2dLevel != null) levels.push(sitemap2dLevel);
            // Find newest 3d version
            const sitemap3dLevel = getNewestSitemapVersionLevelByType(sitemap, currentLevel, SitemapVersionType.ThreeDimensional);
            if (sitemap3dLevel != null) levels.push(sitemap3dLevel);
            return levels;
        }

        if (!config.version2dVisible && !config.version3dVisible) {
            return [];
        }

        const version2d = sitemap.versions.find((v) => v.id === config.version2dId);
        const level2d = version2d?.sitemapVersionLevels.find((l) => l.levelIndex === currentLevel);

        const version3d = sitemap.versions.find((v) => v.id === config.version3dId);
        const level3d = version3d?.sitemapVersionLevels.find((l) => l.levelIndex === currentLevel);
        if (level2d && config.version2dVisible) {
            levels.push({
                id: level2d.id,
                type: SitemapVersionType.TwoDimensional,
                bounding: level2d.bounding,
                sitemapName: sitemap.name,
                sitemapVersionName: version2d.name,
                perimeter: level2d.perimeter,
                levelIndex: level2d.levelIndex,
                mapOrder: sitemap.mapOrder,
            });
        }
        if (level3d && config.version3dVisible) {
            levels.push({
                id: level3d.id,
                type: SitemapVersionType.ThreeDimensional,
                bounding: level3d.bounding,
                sitemapName: sitemap.name,
                sitemapVersionName: version3d.name,
                perimeter: level3d.perimeter,
                levelIndex: level3d.levelIndex,
                mapOrder: sitemap.mapOrder,
            });
        }
        return levels;
    });
}

function getNewestSitemapVersionLevelByType(sitemap: Sitemap, currentLevel: number, type: SitemapVersionType) {
    // Find the newest version of the chosen type
    const sortedVersions = sitemap.versions.filter((version) => version.type === type).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    if (sortedVersions.length > 0) {
        const newestSitemapVersion = sortedVersions[0];
        // Within the newest version find the selected level
        const level = newestSitemapVersion.sitemapVersionLevels.find((svl) => svl.levelIndex === currentLevel);
        return level
            ? {
                  id: level.id,
                  type,
                  sitemapName: sitemap.name,
                  sitemapVersionName: newestSitemapVersion.name,
                  bounding: level.bounding,
                  perimeter: level.perimeter,
                  levelIndex: level.levelIndex,
                  mapOrder: sitemap.mapOrder,
              }
            : null;
    }
    return null;
}

export function getSelectedSitemapVersionLevelPerimeters(sitemaps: Sitemap[], selectedSitemapVersionLevels: { id: string; type: SitemapVersionType }[]) {
    const selectedLevels = sitemaps.flatMap((sitemap) =>
        sitemap.versions.flatMap((version) =>
            version.sitemapVersionLevels.filter((sitemapVersionLevel) => selectedSitemapVersionLevels.some(({ id }) => id === sitemapVersionLevel.id))
        )
    );
    return selectedLevels.map((level) => level.perimeter);
}

/** Updates to the selected level and sets the selected sitemap version levels */
export function updateSelectedLevel(state: MapGlobalState, updatedLevel: number) {
    if (state.mapModule.currentLevel !== updatedLevel) {
        state.mapModule.currentLevel = updatedLevel;
    }

    // Update the selected sitemap version levels
    const selectedSitemapVersionLevels = getSelectedSitemapVersionLevels(state.sitemapsInView, state.sitemapConfigs, state.mapModule.currentLevel);
    stampValue(state.mapModule.selectedSitemapVersionLevels, selectedSitemapVersionLevels);
    stampValue(state.mapModule.sitemapVersionLevelPerimeters, getSelectedSitemapVersionLevelPerimeters(state.sitemapsInView, state.mapModule.selectedSitemapVersionLevels.value));
    // If a selected sitemap version level is 3D
    if (selectedSitemapVersionLevels.some((svl) => svl.type === SitemapVersionType.ThreeDimensional)) {
        // Terrain has to be off
        if (state.mapModule.terrain3D.value.enabled) {
            stampValue(state.mapModule.terrain3D, { ...state.mapModule.terrain3D.value, enabled: false });
        }

        // 3D buildings need to be off
        if (state.mapModule.buildings3D.value) {
            stampValue(state.mapModule.buildings3D, false);
        }

        // Ensure the background we have selected is allowed with a 3D sitemap, if not pick one that is
        const selectedMapBackground = state.mapBackgrounds.find((background) => background.id === state.mapModule.selectedMapBackgroundId);
        if (selectedMapBackground != null && !selectedMapBackground?.metaData?.["3dSitemapCompatible"]) {
            // Attempt to get Google Satellite map background, if we can't find it then just get any 3D sitemap compatible map background
            const allowedMapBackground =
                state.mapBackgrounds.find((background) => background?.name === GOOGLE_MAPS_SATELLITE_MAP_BACKGROUND_NAME) ??
                state.mapBackgrounds.find((background) => background?.metaData?.["3dSitemapCompatible"]);
            if (allowedMapBackground) {
                state.mapModule.selectedMapBackgroundId = allowedMapBackground.id;
            }
        }
    }
}
